import React, { useState, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { grafikService } from '../../../services/grafikService';
import {
  calculateShiftHours, getAllStatuses, parseShiftTime, formatHours, toISODate
} from '../../../utils/grafikHelpers';

/**
 * Modal: utworzenie / edycja pojedynczej zmiany.
 * Props:
 *   mode: 'create' | 'edit'
 *   initialData: {id?, user_login?, location_id?, data?, godzina_od?, godzina_do?, przerwa_minut?, status?, typ_zmiany?, notatki?}
 *   users: lista pracownikow [{login, typ}]
 *   locations: [{id, nazwa, ...}]
 *   onClose()
 *   onSaved()  - po zapisie/usuwaniu
 */
const ShiftEditModal = ({ mode = 'create', initialData = {}, users = [], locations = [], onClose, onSaved }) => {
  const [form, setForm] = useState({
    user_login: initialData.user_login || '',
    location_id: initialData.location_id || '',
    data: initialData.data || toISODate(new Date()),
    godzina_od: parseShiftTime(initialData.godzina_od) || '08:00',
    godzina_do: parseShiftTime(initialData.godzina_do) || '16:00',
    przerwa_minut: initialData.przerwa_minut ?? 0,
    status: initialData.status || 'planowana',
    typ_zmiany: initialData.typ_zmiany || '',
    notatki: initialData.notatki || ''
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  // Soft-walidacja: wpis godzin otwarcia dla wybranego dnia tygodnia
  const [locationHoursForDay, setLocationHoursForDay] = useState(null);
  // Inni pracownicy tego samego dnia w tej samej lokalizacji
  const [sameDayStaff, setSameDayStaff] = useState([]);
  const [confirmMultiStaff, setConfirmMultiStaff] = useState(false);
  const debounceRef = useRef(null);
  const sameDayDebounceRef = useRef(null);

  const isEdit = mode === 'edit' && initialData.id;

  // Sledzimy czy user recznie edytowal godziny - jesli tak, NIE nadpisujemy
  // domyslnymi z location_godziny_pracy.
  const userEditedTimesRef = useRef(false);

  // Pre-fill godzin domyslnymi z location_godziny_pracy gdy zmieni sie
  // lokalizacja albo data. W trybie create + bez recznej edycji - wstaw godziny
  // do formularza. Zawsze - zapisuj entry dnia tygodnia do soft-walidacji.
  useEffect(() => {
    if (!form.location_id || !form.data) {
      setLocationHoursForDay(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await grafikService.getLocationHours(form.location_id);
        const hoursList = Array.isArray(res?.data) ? res.data
                        : Array.isArray(res?.data?.data) ? res.data.data
                        : Array.isArray(res) ? res
                        : [];
        if (cancelled) return;
        if (!hoursList || hoursList.length === 0) {
          setLocationHoursForDay(null);
          return;
        }

        // dzien tygodnia z formy: 1=pn..7=nd
        const d = new Date(form.data + 'T00:00:00');
        const jsDay = d.getDay(); // 0=ndz..6=sob
        const dt = jsDay === 0 ? 7 : jsDay;
        const entry = hoursList.find(h => Number(h.dzien_tygodnia) === dt) || null;
        setLocationHoursForDay(entry);

        // Pre-fill tylko w create i jesli user nie edytowal recznie
        if (isEdit) return;
        if (userEditedTimesRef.current) return;
        if (!entry || entry.zamkniete) return;
        if (!entry.godzina_od || !entry.godzina_do) return;

        setForm(prev => ({
          ...prev,
          godzina_od: parseShiftTime(entry.godzina_od) || prev.godzina_od,
          godzina_do: parseShiftTime(entry.godzina_do) || prev.godzina_do
        }));
      } catch (err) {
        // Bez konfiguracji godzin pracy - zostawiamy default 8-16
        if (!cancelled) setLocationHoursForDay(null);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.location_id, form.data, isEdit]);

  // Soft-walidacja: czy zmiana wykracza poza godziny otwarcia lokalizacji.
  const outsideHoursWarning = useMemo(() => {
    const entry = locationHoursForDay;
    if (!entry) return null;
    const dayNamesPl = {
      1: 'poniedzialki', 2: 'wtorki', 3: 'srody', 4: 'czwartki',
      5: 'piatki', 6: 'soboty', 7: 'niedziele'
    };
    const dayName = dayNamesPl[Number(entry.dzien_tygodnia)] || '';
    if (entry.zamkniete) {
      return `Sklep zamkniety w ${dayName} - zmiana moze byc bledna.`;
    }
    const op = parseShiftTime(entry.godzina_od);
    const cl = parseShiftTime(entry.godzina_do);
    if (!op || !cl) return null;
    const od = (form.godzina_od || '').slice(0, 5);
    const dop = (form.godzina_do || '').slice(0, 5);
    if (!od || !dop) return null;
    if (od < op || dop > cl) {
      return `Zmiana ${od}-${dop} wykracza poza godziny otwarcia (lokalizacja czynna ${op}-${cl} w ${dayName}).`;
    }
    return null;
  }, [locationHoursForDay, form.godzina_od, form.godzina_do]);

  const totalHours = useMemo(() => calculateShiftHours(
    form.godzina_od, form.godzina_do, Number(form.przerwa_minut || 0)
  ), [form.godzina_od, form.godzina_do, form.przerwa_minut]);

  // Debounce check innych pracownikow tego samego dnia w lokalizacji
  useEffect(() => {
    if (!form.location_id || !form.data) {
      setSameDayStaff([]);
      setConfirmMultiStaff(false);
      return;
    }
    if (sameDayDebounceRef.current) clearTimeout(sameDayDebounceRef.current);
    sameDayDebounceRef.current = setTimeout(async () => {
      try {
        const res = await grafikService.getSameDayStaff({
          locationId: form.location_id,
          data: form.data,
          excludeUserLogin: form.user_login || undefined,
          excludeShiftId: initialData.id
        });
        const arr = res?.data || res || [];
        setSameDayStaff(Array.isArray(arr) ? arr : []);
        // Reset checkbox przy zmianie kontekstu
        setConfirmMultiStaff(false);
      } catch (err) {
        console.warn('[ShiftEditModal] same-day-staff:', err.message);
        setSameDayStaff([]);
      }
    }, 500);
    return () => {
      if (sameDayDebounceRef.current) clearTimeout(sameDayDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.location_id, form.data, form.user_login]);

  // Debounce check conflicts
  useEffect(() => {
    if (!form.user_login || !form.data || !form.godzina_od || !form.godzina_do) {
      setConflicts([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setCheckingConflicts(true);
      try {
        const res = await grafikService.checkConflicts({
          userLogin: form.user_login,
          data: form.data,
          godzinaOd: form.godzina_od,
          godzinaDo: form.godzina_do,
          excludeId: initialData.id
        });
        const arr = res?.data?.conflicts || res?.conflicts || res?.data || [];
        setConflicts(Array.isArray(arr) ? arr : []);
      } catch (err) {
        // silent fail - to tylko pomocniczo
        console.warn('[ShiftEditModal] Konflikty:', err.message);
        setConflicts([]);
      } finally {
        setCheckingConflicts(false);
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.user_login, form.data, form.godzina_od, form.godzina_do]);

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.user_login || !form.location_id || !form.data) {
      toast.error('Wypelnij pola: pracownik, lokalizacja, data');
      return;
    }
    // Walidacja: inni pracownicy tego samego dnia + lokalizacji
    const isAbsence = ['urlop', 'chorobowe', 'nieobecnosc', 'wolne'].includes(form.status);
    if (!isAbsence && sameDayStaff.length > 0 && !confirmMultiStaff) {
      toast.error('Potwierdz checkboxem ze chcesz dodac kolejnego pracownika na ten dzien');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        user_login: form.user_login,
        location_id: Number(form.location_id) || form.location_id,
        data: form.data,
        godzina_od: form.godzina_od,
        godzina_do: form.godzina_do,
        przerwa_minut: Number(form.przerwa_minut) || 0,
        status: form.status,
        typ_zmiany: form.typ_zmiany || null,
        notatki: form.notatki || null
      };
      if (isEdit) {
        await grafikService.updateShift(initialData.id, payload);
        toast.success(`Zmiana zaktualizowana. Powiadomienie wyslane do ${form.user_login}.`);
      } else {
        await grafikService.createShift(payload);
        toast.success(`Zmiana utworzona. Powiadomienie wyslane do ${form.user_login}.`);
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error(err.message || 'Blad zapisu zmiany');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm('Usunac te zmiane? Operacja nieodwracalna.')) return;
    setDeleting(true);
    try {
      await grafikService.deleteShift(initialData.id);
      toast.success(`Zmiana usunieta. Powiadomienie wyslane do ${form.user_login || 'pracownika'}.`);
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error(err.message || 'Blad usuwania zmiany');
    } finally {
      setDeleting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '0.5rem',
    border: '1px solid var(--border)', borderRadius: '4px',
    backgroundColor: 'var(--card-bg)', color: 'var(--text)',
    fontSize: '0.9rem'
  };

  const labelStyle = {
    display: 'block', marginBottom: '0.25rem',
    fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)'
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1001,
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      padding: '1rem'
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
    >
      <div style={{
        backgroundColor: 'var(--card-bg)',
        borderRadius: '8px',
        width: '100%', maxWidth: '600px',
        maxHeight: '90vh', overflowY: 'auto',
        border: '1px solid var(--border)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid var(--border)',
          backgroundColor: 'var(--bg)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
            {isEdit ? 'Edytuj zmiane' : 'Nowa zmiana'}
          </h4>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '1.2rem', cursor: 'pointer' }}>
            &#x2715;
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Pracownik *</label>
              <select
                value={form.user_login}
                onChange={(e) => updateField('user_login', e.target.value)}
                disabled={isEdit}
                style={inputStyle}
              >
                <option value="">— wybierz —</option>
                {users.map(u => (
                  <option key={u.login} value={u.login}>
                    {u.login}{u.typ ? ` (${u.typ})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Lokalizacja *</label>
              <select
                value={form.location_id}
                onChange={(e) => updateField('location_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— wybierz —</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.nazwa || l.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Data *</label>
              <input type="date" value={form.data} onChange={(e) => updateField('data', e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Od</label>
              <input
                type="time"
                value={form.godzina_od}
                onChange={(e) => { userEditedTimesRef.current = true; updateField('godzina_od', e.target.value); }}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Do</label>
              <input
                type="time"
                value={form.godzina_do}
                onChange={(e) => { userEditedTimesRef.current = true; updateField('godzina_do', e.target.value); }}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Przerwa (min)</label>
              <input
                type="number" min="0" step="5"
                value={form.przerwa_minut}
                onChange={(e) => updateField('przerwa_minut', e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select value={form.status} onChange={(e) => updateField('status', e.target.value)} style={inputStyle}>
                {getAllStatuses().map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Typ zmiany</label>
              <input
                type="text"
                placeholder="np. 8h dzienna"
                value={form.typ_zmiany}
                onChange={(e) => updateField('typ_zmiany', e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Notatki</label>
            <textarea
              rows={2}
              value={form.notatki}
              onChange={(e) => updateField('notatki', e.target.value)}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{
            padding: '0.5rem 0.75rem',
            backgroundColor: 'var(--bg)',
            borderRadius: '4px',
            fontSize: '0.85rem', color: 'var(--text-secondary)',
            marginBottom: '0.75rem'
          }}>
            <i className="fas fa-clock" style={{ marginRight: '0.4rem' }} />
            Godziny planowane: <strong style={{ color: 'var(--info)' }}>{formatHours(totalHours)}</strong>
            {checkingConflicts && (
              <span style={{ marginLeft: '1rem', fontStyle: 'italic' }}>
                Sprawdzam konflikty...
              </span>
            )}
          </div>

          {outsideHoursWarning && (
            <div style={{
              padding: '0.6rem 0.8rem',
              backgroundColor: 'rgba(255,193,7,0.12)',
              borderLeft: '3px solid var(--warning)',
              color: 'var(--text)',
              borderRadius: '4px',
              fontSize: '0.85rem',
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.5rem'
            }}>
              <i
                className="fas fa-exclamation-triangle"
                style={{ color: 'var(--warning)', marginTop: '0.15rem' }}
              />
              <div>
                <strong style={{ color: 'var(--warning)' }}>Uwaga:</strong>{' '}
                {outsideHoursWarning}
                <div style={{ marginTop: '0.25rem', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                  Ostrzezenie nie blokuje zapisu - mozesz kontynuowac.
                </div>
              </div>
            </div>
          )}

          {conflicts.length > 0 && (
            <div style={{
              padding: '0.6rem 0.8rem',
              backgroundColor: 'rgba(255,193,7,0.15)',
              border: '1px solid var(--warning)',
              color: 'var(--warning)',
              borderRadius: '4px',
              fontSize: '0.85rem',
              marginBottom: '0.75rem'
            }}>
              <strong>Uwaga: konflikty</strong>
              <ul style={{ margin: '0.4rem 0 0 1rem', padding: 0 }}>
                {conflicts.map(c => (
                  <li key={c.id || `${c.data}-${c.godzina_od}`}>
                    {c.data} {parseShiftTime(c.godzina_od)}-{parseShiftTime(c.godzina_do)}
                    {c.location_nazwa ? ` w ${c.location_nazwa}` : ''}
                    {c.user_login ? ` (${c.user_login})` : ''}
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: '0.4rem', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                Zapis nie jest blokowany — admin moze zatwierdzic mimo konfliktu.
              </div>
            </div>
          )}

          {sameDayStaff.length > 0 && !['urlop','chorobowe','nieobecnosc','wolne'].includes(form.status) && (
            <div style={{
              padding: '0.6rem 0.8rem',
              backgroundColor: 'var(--info-light)',
              border: '1px solid var(--info)',
              borderLeft: '3px solid var(--info)',
              color: 'var(--text)',
              borderRadius: '4px',
              fontSize: '0.85rem',
              marginBottom: '0.75rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem' }}>
                <i className="fas fa-users" style={{ color: 'var(--info)', marginTop: '0.15rem' }} />
                <div style={{ flex: 1 }}>
                  <strong style={{ color: 'var(--info)' }}>
                    W tym dniu sa juz zaplanowani inni pracownicy ({sameDayStaff.length}):
                  </strong>
                  <ul style={{ margin: '0.3rem 0 0 1rem', padding: 0 }}>
                    {sameDayStaff.map(s => (
                      <li key={s.id}>
                        <strong>{s.user_login}</strong>
                        {s.godzina_od && s.godzina_do ? ` ${s.godzina_od}-${s.godzina_do}` : ''}
                        {s.status && s.status !== 'planowana' ? ` (${s.status})` : ''}
                      </li>
                    ))}
                  </ul>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    marginTop: '0.5rem', padding: '0.4rem 0.5rem',
                    backgroundColor: 'var(--card-bg)', borderRadius: '4px',
                    cursor: 'pointer', fontWeight: 600
                  }}>
                    <input
                      type="checkbox"
                      checked={confirmMultiStaff}
                      onChange={(e) => setConfirmMultiStaff(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    Tak, dodaj kolejnego pracownika na ten dzien
                  </label>
                </div>
              </div>
            </div>
          )}

          <div style={{
            display: 'flex', justifyContent: 'space-between', gap: '0.5rem',
            paddingTop: '0.75rem', borderTop: '1px solid var(--border)'
          }}>
            <div>
              {isEdit && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid var(--danger)', color: 'var(--danger)',
                    backgroundColor: 'transparent', cursor: 'pointer',
                    fontSize: '12px', fontWeight: 500
                  }}
                >
                  {deleting ? 'Usuwam...' : 'Usun'}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button" onClick={onClose}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)',
                  color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px'
                }}
              >
                Anuluj
              </button>
              <button
                type="submit" disabled={saving}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'var(--info)', color: '#fff',
                  border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600
                }}
              >
                {saving ? 'Zapisuje...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShiftEditModal;
